import React, { useState, useEffect } from "react";
import {
  Drawer,
  Button,
  Space,
  Select,
  Typography,
  Popover,
  Divider,
  DatePicker,
} from "antd";
import {
  LoginOutlined,
  PlayCircleFilled,
  ThunderboltOutlined,
  CloseOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import WorkspacePage from "../../Pages/Workspace/WorkspacePage";
import CheckInService from "../../Services/Manage/CheckInService";
import BreakTypeService from "../../Services/Manage/BreakTypeService";

const { Text, Title } = Typography;

const activityOptions = [
  { label: "Work", value: "work" },
  { label: "Meeting", value: "meeting" },
  { label: "Group Discussion", value: "group" },
  { label: "Available for collab", value: "collab" },
  { label: "Do not disturb", value: "donot_distrub" },
  { label: "Leave", value: "leave" },
];

const QuickAdd = () => {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState("00:00:00");

  const [breakOptions, setBreakOptions] = useState([]);
  const [breakType, setBreakType] = useState(null);

  const [activityType, setActivityType] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentCheckInId, setCurrentCheckInId] = useState(null);
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const omeUser = JSON.parse(localStorage.getItem("ome_user"));
  const empUid = omeUser?.uid;

  const [breakMap, setBreakMap] = useState({});
  const [breaksDurationMap, setBreaksDurationMap] = useState({});

  const [breakHistory, setBreakHistory] = useState([]);

  useEffect(() => {
    const fetchCheckInStatus = async () => {
      try {
        const res = await CheckInService.getCheckInStatus(empUid);
        if (res.status && res.data?.checkIn) {
          const {
            check_in_time,
            check_out_time,
            id,
            last_break_type,
            last_activity_type,
          } = res.data.checkIn;

          setCheckInTime(
            check_in_time ? new Date(check_in_time).getTime() : null
          );
          setCheckOutTime(
            check_out_time ? new Date(check_out_time).getTime() : null
          );
          setCurrentCheckInId(id);
          setCheckedIn(!check_out_time);

          const savedBreakType =
            localStorage.getItem("quickAdd_breakType");
          const savedActivityType =
            localStorage.getItem("quickAdd_activityType");

          if (savedBreakType) {
            setBreakType(savedBreakType);
          } else if (last_break_type) {
            setBreakType(last_break_type);
            localStorage.setItem("quickAdd_breakType", last_break_type);
          }

          if (savedActivityType) {
            setActivityType(savedActivityType);
          } else if (last_activity_type) {
            setActivityType(last_activity_type);
            localStorage.setItem(
              "quickAdd_activityType",
              last_activity_type
            );
          }
        } else {
          setCheckedIn(false);
          setCheckInTime(null);
          setCheckOutTime(null);
          setCurrentCheckInId(null);
          setBreakType(null);
          setActivityType(null);
          localStorage.removeItem("quickAdd_breakType");
          localStorage.removeItem("quickAdd_activityType");
        }
      } catch (error) {
        console.error("Failed to fetch check-in status", error);
      }
    };

    fetchCheckInStatus();
  }, [empUid]);

  useEffect(() => {
    const fetchBreakTitles = async () => {
      try {
        const res = await BreakTypeService.getAllBreaks();
        if (res.data) {
          const options = res.data.map((breakItem) => {
            const durationObj = breakItem["breaks_in_hour/min"] || {};
            const hours = durationObj.hours || 0;
            const minutes = durationObj.minutes || 0;

            let durationStr = "";
            if (hours > 0) durationStr += `${hours}h `;
            if (minutes > 0) durationStr += `${minutes}m`;

            return {
              label: `${breakItem.title} ${durationStr ? `(${durationStr.trim()})` : ""}`,
              value: breakItem.id?.toString() || breakItem.key,
              breaks_in_hour_min: durationObj,
            };
          });
          setBreakOptions(options);

          const map = {};
          const breaksDurationMap = {};
          res.data.forEach((breakItem) => {
            const id = breakItem.id?.toString() || breakItem.key;
            map[id] = breakItem.title;
            breaksDurationMap[id] = breakItem["breaks_in_hour/min"] || {};
          });
          setBreakMap(map);
          setBreaksDurationMap(breaksDurationMap);
        }
      } catch (err) {
        console.error("Failed to fetch break options", err);
      }
    };

    fetchBreakTitles();
  }, []);


  useEffect(() => {
    let timer;
    if (checkedIn && checkInTime) {
      timer = setInterval(() => {
        const diff = Date.now() - checkInTime;
        const hours = String(Math.floor(diff / 3600000)).padStart(2, "0");
        const minutes = String(
          Math.floor((diff % 3600000) / 60000)
        ).padStart(2, "0");
        const seconds = String(
          Math.floor((diff % 60000) / 1000)
        ).padStart(2, "0");
        setElapsedTime(`${hours}:${minutes}:${seconds}`);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [checkedIn, checkInTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchCheckInHistory = async () => {
    try {
      const today = selectedDate || new Date();
      const formattedToday = today.toISOString().split("T")[0]; // YYYY-MM-DD
      const res = await CheckInService.getCheckInHistoryByDate(empUid, formattedToday);

      if (res.status && res.data.length) {
        const structured = res.data[0].data.map((entry, index) => ({
          id: `${formattedToday}-${index}`,
          check_in_time: entry.check_in_time,
          check_out_time: entry.check_out_time,
          breaks: entry.breaks || [],
        }));

        setFilteredHistory(structured);

        // Flatten all breaks/activity logs into one array
        const allLogs = [];
        structured.forEach((entry, idx) => {
          (entry.breaks || []).forEach((log, logIdx) => {
            allLogs.push({
              id: `${entry.id || formattedToday}-${logIdx}`,
              checkInTime: entry.check_in_time,
              checkOutTime: entry.check_out_time,
              status: log.status || "Break",
              activity: log.activity,
              log_time: log.log_time,
              duration: log.duration,
            });

          });
        });

        setBreakHistory(allLogs);
      } else {
        setFilteredHistory([]);
        setBreakHistory([]);
      }
    } catch (err) {
      console.error("Failed to fetch history:", err);
      setFilteredHistory([]);
      setBreakHistory([]);
    }
  };


  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleHistoryOpen = () => {
    fetchCheckInHistory();
    setHistoryOpen(true);
  };
  const handleHistoryClose = () => setHistoryOpen(false);

  const handleCheckIn = async () => {
    try {
      if (!checkedIn) {
        const payload = {
          user_id: empUid,
          check_in_time: new Date().toISOString(),
        };
        const res = await CheckInService.createCheckIn(payload);
        if (res.status) {
          setCheckInTime(new Date(res.data.check_in_time).getTime());
          setCheckedIn(true);
          setCurrentCheckInId(res.data.id);
        }
      } else {
        const payload = { check_out_time: new Date().toISOString() };
        const res = await CheckInService.updateCheckIn(empUid, payload);
        if (res.status) {
          setCheckedIn(false);
          setCheckInTime(null);
          setElapsedTime("00:00:00");
          setCurrentCheckInId(null);
          setCheckOutTime(new Date(payload.check_out_time).getTime());

          setBreakType(null);
          setActivityType(null);
          localStorage.removeItem("quickAdd_breakType");
          localStorage.removeItem("quickAdd_activityType");
        }
      }
    } catch (err) {
      console.error("Error during check-in/out:", err);
    }
  };

  const fetchLogsForCurrentCheckIn = async () => {
    if (!currentCheckInId) return;

    try {
      const res = await CheckInService.getCheckInHistoryWithLogs(empUid);
      if (res.status) {
        const currentCheckInLogs = res.data.find(
          (checkIn) => checkIn.id === currentCheckInId
        );

        if (currentCheckInLogs) {
          const logsFormatted = currentCheckInLogs.logs.map((log, idx) => ({
            id: `${currentCheckInId}-log-${idx}`,
            status: log.type || log.status || "Break",
            activity: log.activity || "",
            log_time: log.log_time,
            breaks_in_hour_min: log.breaks_in_hour_min || {},
          }));

          setBreakHistory(logsFormatted);
        }
      }
    } catch (err) {
      console.error("Failed to fetch logs for current check-in", err);
    }
  };



  const handleBreakSelect = async (val) => {
    setBreakType(val);
    localStorage.setItem("quickAdd_breakType", val);


    const selectedBreakDuration =
      breaksDurationMap[val] || { hours: 0, minutes: 0 };


    const durationInSeconds =
      (selectedBreakDuration.hours || 0) * 3600 +
      (selectedBreakDuration.minutes || 0) * 60;


    const breakTitle = breakMap[val] || "Break";

    const payload = {
      check_in_id: currentCheckInId,
      type: "Break",
      status: breakTitle,
      log_time: new Date().toISOString(),
      duration: durationInSeconds,
    };

    try {
      const res = await CheckInService.addCheckInLog(payload);
      if (res.status) {
        console.log("✅ Break logged with duration:", durationInSeconds);
        await fetchLogsForCurrentCheckIn();
      } else {
        console.error("❌ Failed to log break:", res);
      }
    } catch (err) {
      console.error("Error logging break:", err);
    }
  };



  const handleActivitySelect = async (val) => {
    setActivityType(val);
    localStorage.setItem("quickAdd_activityType", val);

    const payload = {
      check_in_id: currentCheckInId,
      type: "activity",
      activity: val,
      log_time: new Date().toISOString(),
    };

    try {
      await CheckInService.addCheckInLog(payload);

      await fetchLogsForCurrentCheckIn();
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  };


  const handleDateFilter = async (date) => {
    setSelectedDate(date);

    if (!date) {
      setFilteredHistory(history);
      setBreakHistory([]);
      return;
    }

    try {
      const formattedDate = date.format("YYYY-MM-DD");
      const res = await CheckInService.getCheckInHistoryByDate(
        empUid,
        formattedDate
      );

      if (res.status && res.data.length) {
        const structured = res.data[0].data.map((entry, index) => ({
          id: `${formattedDate}-${index}`,
          check_in_time: entry.check_in_time,
          check_out_time: entry.check_out_time,
          breaks: entry.breaks || [],
        }));
        setFilteredHistory(structured);

        // Flatten all logs including activities
        const allLogs = [];
        structured.forEach((entry, idx) => {
          (entry.breaks || []).forEach((log, logIdx) => {
            allLogs.push({
              id: `${formattedDate}-${idx}-${logIdx}`,
              checkInTime: entry.check_in_time,
              checkOutTime: entry.check_out_time,
              status: log.status || log.type || "Break",
              activity: log.activity || "",
              log_time: log.log_time,
              duration: log.duration,
            });
          });
        });
        setBreakHistory(allLogs);
      } else {
        setFilteredHistory([]);
        setBreakHistory([]);
      }
    } catch (error) {
      console.error("Error fetching filtered history:", error);
      setFilteredHistory([]);
      setBreakHistory([]);
    }
  };


  const popoverContent = (
    <div
      style={{
        maxWidth: 300,
        padding: 20,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        fontFamily:
          "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
        color: "#333",
      }}
    >
      <Title level={4} style={{ marginBottom: 0 }}>
        {currentTime.toLocaleTimeString([], { hour12: false })}
      </Title>
      <Text
        type="secondary"
        style={{
          fontSize: 14,
          marginBottom: 12,
          display: "block",
        }}
      >
        {currentTime.toLocaleDateString(undefined, {
          weekday: "long",
          year: "numeric",
          month: "short",
          day: "numeric",
        })}
      </Text>
      {checkedIn && checkInTime && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 8,
            backgroundColor: "#e6f7ff",
            border: "1px solid #91d5ff",
          }}
        >
          <Text strong style={{ color: "#1890ff" }}>
            Logged in at:{" "}
            <Text strong style={{ color: "#1890ff" }}>
              {new Date(checkInTime).toLocaleTimeString([], {
                hour12: false,
              })}
            </Text>
          </Text>
        </div>
      )}
      <Button
        type="primary"
        icon={<HistoryOutlined />}
        block
        style={{ marginBottom: 16, fontWeight: "600" }}
        onClick={() => {
          handleHistoryOpen();
        }}
      >
        Check-In History
      </Button>
      <Divider style={{ margin: "12px 0" }} />
      <Text strong style={{ fontSize: 15, color: "#000" }}>
        Select Break:
      </Text>
      <Select
        placeholder="Choose break"
        style={{ width: "100%", marginTop: 8 }}
        onChange={handleBreakSelect}
        value={breakType}
        options={breakOptions}
        dropdownStyle={{ backgroundColor: "#fff" }}
      />
      <Divider style={{ margin: "20px 0 12px" }} />
      <Text strong style={{ fontSize: 15, color: "#000" }}>
        Select Activity:
      </Text>
      <Select
        placeholder="Choose activity"
        style={{ width: "100%", marginTop: 8 }}
        onChange={handleActivitySelect}
        value={activityType}
        options={activityOptions}
        dropdownStyle={{ backgroundColor: "#fff" }}
      />
    </div>
  );

  return (
    <>
      <Space>
        {checkedIn ? (
          <Space align="center" size="small">
            <Text
              style={{
                color: "#1890ff",
                fontWeight: "600",
                userSelect: "none",
                fontSize: 14,
                backgroundColor: "#e6f7ff",
                padding: "4px 10px",
                borderRadius: 12,
                fontFamily:
                  "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                boxShadow:
                  "0 1px 3px rgba(24, 144, 255, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>Logged in at:</span>
              <span
                style={{
                  fontFamily: "monospace",
                  fontWeight: "700",
                }}
              >
                {checkInTime
                  ? new Date(checkInTime).toLocaleTimeString([], {
                    hour12: false,
                  })
                  : ""}
              </span>
            </Text>

            <Popover
              content={popoverContent}
              title="Check-In Details"
              trigger="hover"
              placement="bottom"
            >
              <PlayCircleFilled
                style={{
                  fontSize: 22,
                  color: "#1890ff",
                  transform: hovered
                    ? "rotate(-90deg)"
                    : "rotate(90deg)",
                  transition: "transform 0.2s",
                  cursor: "pointer",
                  marginLeft: 8,
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              />
            </Popover>
          </Space>
        ) : (
          <PlayCircleFilled
            style={{
              fontSize: 22,
              color: "#bfbfbf",
              transform: "rotate(90deg)",
              transition: "transform 0.2s",
              cursor: "not-allowed",
            }}
          />
        )}

        <Button
          icon={<LoginOutlined style={{ color: "#155601ff" }} />}
          onClick={handleCheckIn}
          size="large"
        >
          {checkedIn ? "Check Out" : "Check In"}
        </Button>
        <Button
          icon={<ThunderboltOutlined style={{ color: "#fa8c16" }} />}
          onClick={handleOpen}
          size="large"
        >
          Quick Add
        </Button>
      </Space>

      <Drawer
        title="Previous Check-In History"
        placement="right"
        onClose={handleHistoryClose}
        open={historyOpen}
        width={420}
        zIndex={2000}
        bodyStyle={{
          padding: 24,
          display: "flex",
          flexDirection: "column",
        }}
        headerStyle={{ borderBottom: "1px solid #1e1f21ff" }}
        closeIcon={
          <CloseOutlined
            style={{ color: "#040404ff", fontSize: 19 }}
          />
        }
      >
        <DatePicker
          onChange={handleDateFilter}
          style={{ marginBottom: 16, width: "100%" }}
          allowClear
        />

        {filteredHistory.length === 0 ? (
          <Text
            type="secondary"
            style={{
              fontSize: 16,
              textAlign: "center",
              marginTop: 40,
            }}
          >
            No check-in records found.
          </Text>
        ) : (
          Object.entries(
            filteredHistory.reduce((acc, entry) => {
              const dateKey = new Date(
                entry.check_in_time
              ).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
              if (!acc[dateKey]) acc[dateKey] = [];
              acc[dateKey].push(entry);
              return acc;
            }, {})
          ).map(([date, entries]) => (
            <div
              key={date}
              style={{
                borderRadius: 12,
                padding: 20,
                marginBottom: 24,
                boxShadow:
                  "0 3px 8px rgba(24, 144, 255, 0.1)",
              }}
            >
              <Title level={4} style={{ marginBottom: 12 }}>
                📅 {date}
              </Title>

              <div
                style={{
                  borderTop: "1px solid #adc6ff",
                  paddingTop: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "0 10px",
                    marginBottom: 8,
                  }}
                >
                  <Text strong style={{ minWidth: 80 }}>Check-In</Text>
                  <Text strong style={{ minWidth: 80 }}>Check-Out</Text>
                  <Text strong style={{ minWidth: 80, textAlign: "right" }}>Duration</Text>
                </div>

{entries.map((entry, idx) => {
  const checkInDateObj = new Date(entry.check_in_time);
  const checkOutDateObj = entry.check_out_time ? new Date(entry.check_out_time) : currentTime;

  // Calculate total break duration in milliseconds for this entry
  const totalBreakMs = (entry.breaks || []).reduce((sum, b) => {
    const dur = b.duration ? b.duration * 1000 : 0; // duration stored in seconds
    return sum + dur;
  }, 0);

  // Session duration excluding breaks
  const sessionDurationMs = checkOutDateObj - checkInDateObj - totalBreakMs;

  const sessionHours = Math.floor(sessionDurationMs / 3600000);
  const sessionMinutes = Math.floor((sessionDurationMs % 3600000) / 60000);
  const sessionDurationStr = `${sessionHours > 0 ? sessionHours + "h " : ""}${sessionMinutes}m`;

  const checkInTime = checkInDateObj.toLocaleTimeString([], { hour12: false });
  const checkOutTime = checkOutDateObj
    ? checkOutDateObj.toLocaleTimeString([], { hour12: false })
    : "OnGoing";

  return (
    <div
      key={entry.id || idx}
      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: idx !== entries.length - 1 ? "1px dashed #adc6ff" : "none" }}
    >
      {/* Check-In */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <HistoryOutlined style={{ color: "#096dd9", fontSize: 18 }} />
        <Text strong style={{ minWidth: 80 }}>{checkInTime}</Text>
      </div>

      {/* Check-Out */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <LoginOutlined style={{ color: "#52c41a", fontSize: 18 }} />
        <Text strong style={{ minWidth: 80 }}>{checkOutTime}</Text>
      </div>

      {/* Duration */}
      <div style={{ textAlign: "right", minWidth: 80 }}>
        <Text type="secondary">{sessionDurationStr}</Text>
      </div>
    </div>
  );
})}
<Divider style={{ margin: "16px 0" }} />
{(() => {
  const totalWorkedMs = entries.reduce((acc, entry) => {
    const checkIn = new Date(entry.check_in_time);
    const checkOut = entry.check_out_time ? new Date(entry.check_out_time) : currentTime;

    // Sum all breaks for this entry
    const totalBreakMs = (entry.breaks || []).reduce((sum, b) => sum + (b.duration ? b.duration * 1000 : 0), 0);

    return acc + (checkOut - checkIn - totalBreakMs);
  }, 0);

  const totalHours = Math.floor(totalWorkedMs / 3600000);
  const totalMinutes = Math.floor((totalWorkedMs % 3600000) / 60000);
  const totalWorkedStr = `${totalHours > 0 ? totalHours + "h " : ""}${totalMinutes}m`;

  return (
    <div style={{ textAlign: "right", marginTop: 12 }}>
      <Text strong>Total Worked Time: </Text>
      <Text type="secondary">{totalWorkedStr}</Text>
    </div>
  );
})()}



                



              </div>
            </div>
          ))
        )}

        


{/* Divider before Log History */}
<Divider style={{ margin: "24px 0 12px" }} />

<div style={{ marginTop: 24 }}>
  <Title level={4} style={{ marginBottom: 16 }}>Log History</Title>

  <div
    style={{
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 3px 8px rgba(24,144,255,0.1)",
      backgroundColor: "#ffffff",
    }}
  >
    {breakHistory.length === 0 ? (
      <Text type="secondary" style={{ fontSize: 16, textAlign: "center", marginTop: 20 }}>
        No logs found.
      </Text>
    ) : (
      breakHistory.map(({ id, status, log_time, activity, duration }, idx) => {
        const logTimeFormatted = new Date(log_time).toLocaleTimeString([], { hour12: false });
        const isActivity = status === "activity";

        let durationStr = "-";
        if (duration && !isActivity) {
          const hours = Math.floor(duration / 3600);
          const minutes = Math.floor((duration % 3600) / 60);
          durationStr = `${hours > 0 ? hours + "h " : ""}${minutes > 0 ? minutes + "m" : ""}`;
        }

        return (
          <div
            key={id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: idx !== breakHistory.length - 1 ? "1px dashed #adc6ff" : "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isActivity ? (
                <ThunderboltOutlined style={{ color: "#fa8c16", fontSize: 18 }} />
              ) : (
                <LoginOutlined style={{ color: "#52c41a", fontSize: 18 }} />
              )}
              <Text strong style={{ minWidth: 120 }}>
                {activity || status}
              </Text>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Text type="secondary" style={{ minWidth: 80 }}>
                {logTimeFormatted}
              </Text>
              {!isActivity && <Text type="secondary">{durationStr}</Text>}
            </div>
          </div>
        );
      })
    )}

    {/* Divider and total break time at the bottom */}
    {breakHistory.length > 0 && (
      <>
        <Divider style={{ margin: "12px 0" }} />
        {(() => {
          const totalBreakSec = breakHistory.reduce((sum, log) => {
            if (log.duration && log.status !== "activity") return sum + log.duration;
            return sum;
          }, 0);
          const hours = Math.floor(totalBreakSec / 3600);
          const minutes = Math.floor((totalBreakSec % 3600) / 60);
          const totalBreakStr = `${hours > 0 ? hours + "h " : ""}${minutes}m`;
          return (
            <div style={{ textAlign: "right", marginTop: 8 }}>
              <Text strong>Total Break Time: </Text>
              <Text type="secondary">{totalBreakStr}</Text>
            </div>
          );
        })()}
      </>
    )}
  </div>
</div>





      </Drawer>

      <Drawer
        title={
          <Title level={4} style={{ margin: 0 }}>
            Quick Add
          </Title>
        }
        placement="right"
        onClose={handleClose}
        open={open}
        width={520}
        bodyStyle={{ padding: 24, backgroundColor: "#fff" }}
      >
        <WorkspacePage handleClose={handleClose} singleColumn />
      </Drawer>
    </>
  );
};

export default QuickAdd;
